import type { CharterInspiration } from '../types';

/** Render the client's submitted intake responses (formData) as a definition list. */
export function IntakeResponses({ formData }: { formData?: Record<string, unknown> | null }) {
  const entries = formData ? Object.entries(formData) : [];
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No intake responses submitted yet.</p>;
  }
  return (
    <dl className="grid grid-cols-1 gap-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-3 text-sm">
          <dt className="text-muted-foreground min-w-[140px] capitalize">{key.replace(/([A-Z])/g, ' $1')}</dt>
          <dd className="text-foreground break-words">{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map((v) => formatValue(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** The portfolio work the client booked "from", shown as inspiration on the detail. */
export function InspirationPreview({ inspiration }: { inspiration?: CharterInspiration | null }) {
  if (!inspiration) {
    return <p className="text-sm text-muted-foreground">No portfolio work referenced.</p>;
  }
  return (
    <div className="rounded-[12px] border border-border bg-muted p-3">
      <p className="text-sm font-medium text-foreground">{inspiration.title}</p>
      {inspiration.caption && <p className="text-xs text-muted-foreground mt-0.5">{inspiration.caption}</p>}
      {inspiration.media && inspiration.media.length > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap">
          {inspiration.media.map((m) => (
            <img
              key={m.id}
              src={m.cdnUrl}
              alt={inspiration.title}
              className="h-16 w-16 rounded-md object-cover border border-border"
              loading="lazy"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** A small label/value stack for detail panes. */
export function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}
